import { useMemo } from "react";
import { FaFacebookSquare, FaLinkedin } from "react-icons/fa";
import { FaSquareXTwitter } from "react-icons/fa6";
import { IoLogoWhatsapp } from "react-icons/io";
import { Share2 } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  useCbtDetailsQuery,
  useResultQuery,
} from "src/lib/endpoints/question/question.api";
import { getExamRole } from "src/lib/auth/examRole";
import { Card, DashboardLink, Eyebrow, Meter, Shell, Title } from "./ui";

type Extras = { name?: string; percentage?: number };

/**
 * The result, read top-down: the total first, then what makes it up.
 *
 * The old screen opened with four equal sections inside a 60vh scroll box, so
 * the number everyone came for — the total — sat below the fold at the bottom
 * right. Here it leads, and the breakdown explains it.
 *
 * The API returns the breakdown as flat numbered fields (`language1_name`,
 * `language1_percent_score`, …), so collecting them is the same walk the old
 * screen does — as a memo over the result rather than state filled by an
 * effect, which is what made the old lists append twice on a refetch.
 */
const Results = () => {
  const { result } = useResultQuery();
  const { cbtDetails } = useCbtDetailsQuery();
  const isSecondary = getExamRole() === "secondary";

  const { languages, frameworks, skills } = useMemo(() => {
    const out = {
      languages: [] as Extras[],
      frameworks: [] as Extras[],
      skills: [] as Extras[],
    };
    if (!result) return out;

    const collect = (prefix: string, into: Extras[]) => {
      let idx = 1;
      while (`${prefix}${idx}_name` in result) {
        const name = result[`${prefix}${idx}_name`];
        if (name !== null && name !== undefined) {
          into.push({
            name,
            percentage: result[`${prefix}${idx}_percent_score`],
          });
        }
        idx++;
      }
    };

    collect("language", out.languages);
    collect("framework", out.frameworks);
    collect("skill", out.skills);
    return out;
  }, [result]);

  const refUrl = cbtDetails?.refLink;
  const total = result?.total_score;

  const message = `Proud of my result in Droomwork. You think you can beat me or want to hire me? Join now with this link: ${refUrl}\n\nLanguages:${languages
    .map((language) => `\n${language?.name} = ${language?.percentage}%`)
    .join("")}\n\nFrameworks:${frameworks
    .map((framework) => `\n${framework?.name} = ${framework?.percentage}%`)
    .join("")}\n\nSkills:${skills
    .map((skill) => `\n${skill?.name} = ${skill?.percentage}%`)
    .join("")}\n\nOthers:\nInternet = ${
    result?.internet_score
  }%\nVersion Control = ${result?.version_control_score}%\nSecurity = ${
    result?.security_score
  }%\n\nTotal Score = ${result?.total_score}%\n\n`;

  const tags = "#droomwork #talent #client";

  const shareOnTwitter = () =>
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        message
      )}&hashtags=droomwork,talents,client`,
      "_blank"
    );

  const shareOnWhatsapp = () =>
    window.open(
      `https://wa.me/?text=${encodeURIComponent(message + tags)}`,
      "_blank"
    );

  const shareOnLinkedIn = () =>
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?text=${encodeURIComponent(
        message + tags
      )}&url=${encodeURIComponent(refUrl)}`,
      "_blank"
    );

  const shareOnFacebook = () =>
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(refUrl)}`,
      "_blank",
      "noopener,noreferrer"
    );

  const sections = [
    { label: "Specialty", rows: languages },
    { label: "Tools", rows: frameworks },
    { label: "Skill", rows: skills },
    {
      label: "Others",
      rows: [
        { name: "Internet", percentage: result?.internet_score },
        { name: "Version control", percentage: result?.version_control_score },
        { name: "Security", percentage: result?.security_score },
      ] as Extras[],
    },
  ].filter((section) => section.rows.length > 0);

  return (
    <Shell>
      <div className="mb-6 flex items-center justify-between gap-4">
        <Eyebrow>
          {isSecondary ? "Secondary-role assessment result" : "Assessment result"}
        </Eyebrow>
        <DashboardLink />
      </div>

      {!result ? (
        <Card>
          <div className="flex animate-pulse flex-col gap-4">
            <div className="h-10 w-24 rounded bg-brand-navy-100" />
            <div className="h-3 w-full rounded bg-brand-navy-100" />
            <div className="h-3 w-5/6 rounded bg-brand-navy-100" />
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          {/* The total, given the room it earns. */}
          <Card className="flex flex-col gap-5">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <Title className="text-brand-navy">
                  {isSecondary
                    ? "Your secondary-role CBT result"
                    : "Your CBT result"}
                </Title>
                <p className="mt-1 text-sm text-muted-foreground">
                  Across {result.total_number_of_question ?? "all"} questions
                </p>
              </div>

              <div className="text-right">
                <div className="font-display text-5xl font-extrabold leading-none text-brand-navy">
                  {total ?? "—"}
                  <span className="text-2xl">%</span>
                </div>
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Total score
                </div>
              </div>
            </div>

            <Meter value={total ?? 0} tone="orange" label="Total score" />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-navy px-6 text-sm font-semibold text-white transition-colors hover:bg-brand-navy-800">
                    <Share2 className="h-4 w-4" aria-hidden="true" />
                    Share result
                  </button>
                </PopoverTrigger>
                <PopoverContent className="flex w-fit flex-row gap-4 bg-card p-4">
                  <button onClick={shareOnWhatsapp} aria-label="Share on WhatsApp">
                    <IoLogoWhatsapp className="h-7 w-7 text-green-600" />
                  </button>
                  <button onClick={shareOnTwitter} aria-label="Share on X">
                    <FaSquareXTwitter className="h-7 w-7" />
                  </button>
                  <button onClick={shareOnLinkedIn} aria-label="Share on LinkedIn">
                    <FaLinkedin className="h-7 w-7 text-blue-600" />
                  </button>
                  <button onClick={shareOnFacebook} aria-label="Share on Facebook">
                    <FaFacebookSquare className="h-7 w-7 text-blue-600" />
                  </button>
                </PopoverContent>
              </Popover>
            </div>
          </Card>

          {/* The breakdown. One card per group, and the page scrolls — the old
              screen nested a 60vh scroller inside the page, so a long result
              had two scrollbars competing. */}
          {sections.map(({ label, rows }) => (
            <Card key={label} className="flex flex-col gap-4">
              <Eyebrow>{label}</Eyebrow>

              <div className="flex flex-col gap-4">
                {rows.map((row, idx) => (
                  <div key={`${row.name}-${idx}`} className="flex flex-col gap-1.5">
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-sm font-semibold capitalize text-brand-navy">
                        {row.name}
                      </span>
                      <span className="text-sm font-semibold tabular-nums text-brand-navy">
                        {row.percentage ?? 0}%
                      </span>
                    </div>
                    <Meter
                      value={row.percentage ?? 0}
                      tone="orange"
                      label={`${row.name} score`}
                    />
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </Shell>
  );
};

export default Results;
